
import { qualityImages, certificationImages } from '../../../utils/images';

export default function QualityAssurance() {
  const timelineSteps = [
    {
      step: 1,
      title: "Product Design & Development",
      subtitle: "Concept to Prototype",
      description: "Turning ideas and sketches into the first working samples.",
      details: [
        "Sample Process – Covering prototype, sales samples (SMS), pre-production samples (PPS), and TOP samples, ensuring alignment at every stage before bulk production.",
        "Leather Development – In-house tanning, dyeing, and finishing for sheepskin, lambskin, cowhide, PU, and faux fur.",
        "Client Collaboration – Close communication to adjust fit, materials, trims, and finishes quickly."
      ],
      advantage: "Strong material development capabilities, fast response, and flexibility to support both premium collections and mass-market needs.",
      image: qualityImages.design
    },
    {
      step: 2,
      title: "Bulk Production",
      subtitle: "Large-scale Manufacturing Excellence",
      description: "Large-scale manufacturing across leather, shearling, PU, faux fur, down jackets, and woven outerwear.",
      details: [
        "300+ skilled workers and 100,000 m² of facilities combine craftsmanship with efficiency.",
        "Advanced machinery and traditional techniques for premium quality output.",
        "Flexible production lines adaptable to various garment types and volumes."
      ],
      advantage: "Ability to manage both small runs and high-volume orders with consistent quality.",
      image: qualityImages.production
    },
    {
      step: 3,
      title: "Quality Control & Compliance",
      subtitle: "Multi-stage Inspection Process",
      description: "Multi-stage inspection during and after production.",
      details: [
        "Full compliance with international standards: BSCI, Sedex, LWG, RDS, GRS, ISO 9001, LRQA ERSA.",
        "Rigorous testing protocols for durability, safety, and performance.",
        "Continuous monitoring and improvement of quality processes."
      ],
      advantage: "Proven experience working with top US and European brands that demand strict compliance.",
      image: qualityImages.control
    },
    {
      step: 4,
      title: "Shipping & Delivery",
      subtitle: "Global Distribution Excellence",
      description: "Flexible trade terms: FOB and LDP.",
      details: [
        "Expertise in US/EU customs clearance, duties, and logistics.",
        "On-time, transparent, and hassle-free delivery.",
        "Comprehensive documentation and tracking systems."
      ],
      advantage: "Decades of export experience ensuring smooth global distribution.",
      image: qualityImages.shipping
    }
  ];

  const certifications = [
    { name: "BSCI", image: certificationImages.bsci },
    { name: "SEDEX", image: certificationImages.sedex },
    { name: "LWG", image: certificationImages.lwg },
    { name: "RDS", image: certificationImages.rds },
    { name: "GRS", image: certificationImages.grs },
    { name: "ISO 9001", image: certificationImages.iso9001 },
    { name: "ISO 14001", image: certificationImages.iso14001 }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            From Design to Delivery
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            At Musheng, we offer end-to-end OEM & ODM solutions, guiding every project from design concept to final delivery. 
            Our process is built on expertise, transparency, and efficiency, ensuring seamless collaboration with global fashion brands.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Desktop Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-px h-full w-0.5" style={{ backgroundColor: '#c9b570' }}></div>

          {/* Mobile Timeline Line */}
          <div className="lg:hidden absolute left-8 top-0 bottom-0 w-0.5" style={{ backgroundColor: '#c9b570' }}></div>

          {timelineSteps.map((item, index) => (
            <div key={index}>
              {/* Desktop Layout - Left/Right Alternating */}
              <div className={`hidden lg:relative lg:flex lg:items-center lg:mb-16 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                {/* Timeline Dot */}
                <div className="lg:absolute lg:left-1/2 lg:transform lg:-translate-x-1/2 w-12 h-12 border-4 border-white flex items-center justify-center z-10" style={{ backgroundColor: '#c9b570' }}>
                  <span className="text-white font-bold text-lg">{item.step}</span>
                </div>

                {/* Content */}
                <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pr-12' : 'lg:pl-12'}`}>
                  <div className="bg-white p-8 border border-gray-200">
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <h4 className="text-lg font-semibold mb-4" style={{ color: '#c9b570' }}>{item.subtitle}</h4>
                      <p className="text-gray-700 font-medium mb-4">{item.description}</p>
                    </div>

                    <div className="mb-6">
                      <ul className="space-y-3">
                        {item.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start">
                            <i className="ri-arrow-right-s-line text-lg mr-2 mt-0.5 flex-shrink-0" style={{ color: '#c9b570' }}></i>
                            <span className="text-gray-600 text-sm leading-relaxed">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 border-l-4" style={{ backgroundColor: '#f6f2e2', borderLeftColor: '#c9b570' }}>
                      <p className="text-sm font-medium" style={{ color: '#8a7a3f' }}>
                        <span className="font-semibold">Our Advantage:</span> {item.advantage}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image */}
                <div className={`lg:w-1/2 ${index % 2 === 0 ? 'lg:pl-12' : 'lg:pr-12'}`}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-80 object-cover object-top"
                  />
                </div>
              </div>

              {/* Mobile Layout - Timeline on left, content on right in vertical column */}
              <div className="lg:hidden flex mb-12">
                {/* Timeline Dot */}
                <div className="relative flex-shrink-0 mr-6">
                  <div className="w-12 h-12 border-4 border-white flex items-center justify-center z-10" style={{ backgroundColor: '#c9b570' }}>
                    <span className="text-white font-bold text-lg">{item.step}</span>
                  </div>
                </div>

                {/* Content Column - Image + Content */}
                <div className="flex-1 space-y-4">
                  {/* Image */}
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-48 object-cover object-top rounded-lg"
                  />

                  {/* Content */}
                  <div className="bg-white p-6 border border-gray-200 rounded-lg">
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <h4 className="text-base font-semibold mb-3" style={{ color: '#c9b570' }}>{item.subtitle}</h4>
                      <p className="text-gray-700 font-medium mb-3 text-sm">{item.description}</p>
                    </div>

                    <div className="mb-4">
                      <ul className="space-y-2">
                        {item.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start">
                            <i className="ri-arrow-right-s-line text-base mr-2 mt-0.5 flex-shrink-0" style={{ color: '#c9b570' }}></i>
                            <span className="text-gray-600 text-xs leading-relaxed">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="p-3 border-l-4" style={{ backgroundColor: '#f6f2e2', borderLeftColor: '#c9b570' }}>
                      <p className="text-xs font-medium" style={{ color: '#8a7a3f' }}>
                        <span className="font-semibold">Our Advantage:</span> {item.advantage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">International Certifications</h3>
          <div className="lg:flex lg:flex-wrap lg:justify-center lg:gap-8 lg:items-center">
            {/* Mobile Layout: Grid with last item left-aligned */}
            <div className="lg:hidden grid grid-cols-3 gap-4">
              {certifications.map((cert, index) => (
                <div
                  key={index}
                  className={`text-center group flex-shrink-0 ${
                    index === certifications.length - 1 ? 'col-start-1' : ''
                  }`}
                >
                  <div className="w-24 h-24 mx-auto mb-3 group-hover:scale-105 transition-all duration-300">
                    <img
                      src={cert.image}
                      alt={cert.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Layout: Original flex layout */}
            {certifications.map((cert, index) => (
              <div key={index} className="hidden lg:block text-center group flex-shrink-0">
                <div className="w-32 h-32 mx-auto mb-3 group-hover:scale-105 transition-all duration-300">
                  <img
                    src={cert.image}
                    alt={cert.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
